import { useCallback, useEffect } from 'react'
import styled from 'styled-components'
import useEmblaCarousel from 'embla-carousel-react'

import { media } from 'styles/media'

import ChevronDownIcon from '../../icons/ChevronDown'

import ValueCard from './ValueCard'
import values from './values'
import type { PositionProps } from './types'

const Arrow = styled.button.attrs({ type: 'button' })<{
  left?: boolean
  right?: boolean
  disabled?: boolean
}>`
  height: 48px;
  width: 48px;
  padding: 0;

  ${({ left }) =>
    left &&
    `
    left: 16px;

    svg {
      margin-left: -2px;
    }
  `}

  ${({ right }) =>
    right &&
    `
    right: 16px;
    svg {
      margin-right: -2px;
    }
  `}

  appearance: none;
  outline: 0;
  border: 1px solid ${({ theme }) => theme?.['-ds-neutral-500']?.value};
  border-radius: 50%;
  color: ${({ theme }) => theme?.['-ds-neutral-600']?.value};
  background-color: white;
  cursor: pointer;

  svg {
    display: inline-block;
    vertical-align: middle;
    height: 14px;
    margin-top: -2px;
    transform: rotate(${({ left }) => (left ? '90deg' : '-90deg')});
  }

  ${({ disabled }) =>
    disabled &&
    `
    color: #999;
  `}

  &:not(:last-child) {
    margin-right: 16px;
  }
`

const ArrowsWrapper = styled.div<{ $desktopMarginTop?: number }>`
  position: absolute;
  right: 32px;

  ${media.xxl`
    right: calc((100vw - 1300px) / 2);
  `}

  ${({ $desktopMarginTop }) =>
    $desktopMarginTop
      ? `
    top: calc(-50px - ${$desktopMarginTop}px);
  `
      : `
    top: -50px;
  `}
`

const EmblaViewport = styled.div<{ $desktopMarginTop?: number }>`
  overflow: hidden;
  height: 273px;
  ${({ $desktopMarginTop }) =>
    $desktopMarginTop &&
    `
    margin-top: ${$desktopMarginTop}px;
  `};
  overflow-y: hidden;
`

const EmblaContainer = styled.div`
  display: flex;
  gap: 32px;

  & > :first-child {
    margin-left: 32px;

    ${media.xxl`
      margin-left: calc((100vw - 1300px) / 2);
    `}
  }

  & > :last-child {
    margin-right: 32px;
  }
`

const EmblaSlide = styled.div`
  flex: 0 0 auto;
  min-width: 0;
`

const Outer = styled.div`
  width: 100%;
  position: relative;
`

const Slider = ({ desktopMarginTop = 56 }: PositionProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
  })

  const goNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi],
  )
  const goPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi],
  )

  useEffect(() => {
    if (emblaApi) emblaApi.reInit()
  }, [emblaApi])

  return (
    <Outer>
      <ArrowsWrapper $desktopMarginTop={desktopMarginTop}>
        <Arrow onClick={goPrev} left aria-label="slides back">
          <ChevronDownIcon />
        </Arrow>
        <Arrow onClick={goNext} right aria-label="slides forward">
          <ChevronDownIcon />
        </Arrow>
      </ArrowsWrapper>

      <EmblaViewport ref={emblaRef} $desktopMarginTop={desktopMarginTop}>
        <EmblaContainer>
          {values.map(({ text, Icon, backgroundColor }) => (
            <EmblaSlide key={text}>
              <ValueCard
                icon={<Icon />}
                text={text}
                backgroundColor={backgroundColor}
              />
            </EmblaSlide>
          ))}
        </EmblaContainer>
      </EmblaViewport>
    </Outer>
  )
}

export default Slider
